import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, ArrowDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { baseAsset } from "@/lib/utils";

// Shared ladder palette (matches the app teal/green/red/amber/blue scheme).
// CVD-validated against the #0F1620 panel surface; identity is never
// color-alone (legend + tooltip labels + the ● SL badge carry it too).
const LC = {
    text: "#E6EDF6", dim: "#6F7D92",
    // BUY side — colored by the entry strategy that owns the order (`kind`)
    buy: "#2BE38B",        // classic grid rung
    self_heal: "#2EE6D6",  // self-heal re-buy after a profitable cycle
    zone: "#3DA7FF",       // Dalton support-zone entry
    knife: "#FF5CA8",      // knife-catch (crash bounce)
    momentum: "#FF8A3D",   // pump-chase entry
    dd: "#C77DFF",         // double-down pyramid rung
    // SELL side — colored by the exit type managing the order (`exit_kind`)
    sell: "#FF5C6C",       // fixed take-profit
    structure: "#FFB020",  // structure SL-trailing (Dalton state machine)
    runner: "#C77DFF",     // runner trail (double-down / strong-run fills)
    catch: "#FF5CA8",      // knife/pump catch runner (matches its entry hue)
    trail: "#FFB020",      // legacy payloads without exit_kind
};

/** rgba tint from a hex + alpha. */
const tint = (hex, a) => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

// Snapshot field → color category. Fixed order; never cycled.
const BUY_KIND_CAT = {
    grid: "buy", self_heal: "self_heal", zone: "zone",
    knife: "knife", momentum: "momentum", dd: "dd",
};
const EXIT_KIND_CAT = {
    fixed_grid_tp: "sell", trailing_profit: "trail",
    // Backward-compatible rendering for pre-migration payloads.
    tp: "sell", structure: "trail", runner: "trail", catch_runner: "trail",
};

/**
 * Classify an order into a color category. BUYs carry `kind` (entry
 * strategy) and SELLs carry `exit_kind` (exit type) in the grid snapshot;
 * older payloads fall back to origin/is_zone/is_dd + the `trailing` flag
 * (grid_id present in exit_policy).
 */
const orderCategory = (o, side, trailing) => {
    if (side === "BUY") {
        if (BUY_KIND_CAT[o.kind]) return BUY_KIND_CAT[o.kind];
        if (o.is_dd || o.origin === "double_down") return "dd";
        if (o.is_zone || o.origin === "zone") return "zone";
        return "buy";
    }
    if (EXIT_KIND_CAT[o.exit_kind]) return EXIT_KIND_CAT[o.exit_kind];
    if (trailing) return "trail";
    if (o.is_dd) return "dd";
    if (o.is_zone || o.origin === "zone") return "zone";
    return "sell";
};

const CAT_LABEL = {
    buy: "Classic grid BUY", self_heal: "Self-heal re-buy",
    zone: "Dalton zone entry", knife: "Knife-catch entry",
    momentum: "Pump-chase entry", dd: "Double-down rung",
    sell: "Fixed take-profit", structure: "Structure SL-trail",
    runner: "Runner trail", catch: "Catch runner (knife/pump)",
    trail: "Trailing stop active",
};

// Legend rows in fixed order, split by ladder side.
const LEGEND_BUY_ORDER = ["buy", "self_heal", "zone", "knife", "momentum", "dd"];
const LEGEND_SELL_ORDER = ["sell", "structure", "runner", "catch", "trail"];
// A sell exit whose SL is actively managed (shows the ● SL badge).
const MANAGED_EXIT_CATS = new Set(["structure", "runner", "catch", "trail"]);

const pillStyle = (cat, pal = LC) => ({
    background: tint(pal[cat] || LC[cat], cat === "trail" ? 0.16 : 0.10),
    border: `1px solid ${tint(pal[cat] || LC[cat], cat === "trail" ? 0.4 : 0.22)}`,
});

/**
 * Merge registry-supplied strategy colors over the static palette.
 * `strategyColors` maps engine order kinds (grid/self_heal/zone/knife/
 * momentum/dd) to hex — served by /api/smartgrid/strategy-modules so the
 * ladder hue for a strategy always matches its dashboard color chip.
 */
const mergePalette = (strategyColors) => {
    if (!strategyColors) return LC;
    const pal = { ...LC };
    for (const [kind, hex] of Object.entries(strategyColors)) {
        const cat = BUY_KIND_CAT[kind];
        if (cat && /^#[0-9a-fA-F]{6}$/.test(hex)) pal[cat] = hex;
    }
    return pal;
};

/**
 * Hero Grid Ladder visualization.
 * - Vertical price spine with BUY levels below market and active SELL levels above.
 * - BUYs are colored by entry strategy (`kind`): green classic grid, cyan
 *   self-heal, blue Dalton zone, pink knife-catch, orange pump-chase, purple
 *   double-down. SELLs by exit type (`exit_kind`): red fixed TP, amber
 *   structure SL-trail, purple runner trail, pink catch runner. A legend of
 *   the categories present renders along the bottom edge. `trailingIds` is
 *   the legacy fallback for payloads without `exit_kind`.
 * - The current price floats as a glowing cyan line.
 */
export const GridLadder = ({ price, gridSize = 250, buys = [], sells = [], mode = "paper", symbol = "BTCUSDT", trailingIds, strategyColors }) => {
    const pal = useMemo(() => mergePalette(strategyColors), [strategyColors]);
    const trailSet = useMemo(
        () => (trailingIds instanceof Set ? trailingIds : new Set(trailingIds || [])),
        [trailingIds],
    );
    const base = baseAsset(symbol);
    const containerRef = useRef(null);
    const [height, setHeight] = useState(560);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) setHeight(e.contentRect.height);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Compute display window: show +/- (max(5*grid, half range of sells/buys+2 grids))
    const view = useMemo(() => {
        if (!price || !Number.isFinite(price)) return null;
        const allPrices = [
            ...buys.map((b) => b.price),
            ...sells.map((s) => s.price),
            price,
        ];
        const lo = Math.min(...allPrices, price - 5 * gridSize);
        const hi = Math.max(...allPrices, price + 5 * gridSize);
        const pad = gridSize * 1.5;
        const min = lo - pad;
        const max = hi + pad;
        return { min, max, range: max - min };
    }, [price, buys, sells, gridSize]);

    const yOf = (p) => {
        if (!view) return 0;
        const t = (p - view.min) / view.range;
        // top of container = max price; bottom = min price
        return height - t * height;
    };

    // Legend: only the categories actually on the ladder right now.
    const legend = useMemo(() => {
        const present = new Set();
        buys.forEach((b) => present.add(orderCategory(b, "BUY", false)));
        sells.forEach((s) => present.add(orderCategory(s, "SELL", trailSet.has(s.grid_id))));
        return {
            buys: LEGEND_BUY_ORDER.filter((c) => present.has(c)),
            sells: LEGEND_SELL_ORDER.filter((c) => present.has(c)),
        };
    }, [buys, sells, trailSet]);

    // Tick marks every $250
    const ticks = useMemo(() => {
        if (!view) return [];
        const out = [];
        const startTick = Math.ceil(view.min / gridSize) * gridSize;
        for (let v = startTick; v <= view.max; v += gridSize) {
            out.push(v);
        }
        return out;
    }, [view, gridSize]);

    if (!price || !view) {
        return (
            <div className="panel h-[560px] flex items-center justify-center text-[#6F7D92]"
                 data-testid="ladder-container">
                Awaiting first price tick from AsterDex…
            </div>
        );
    }

    return (
        <TooltipProvider delayDuration={150}>
            <div
                ref={containerRef}
                data-testid="ladder-container"
                className="panel relative overflow-hidden"
                style={{ height: 560 }}
            >
                {/* tick scale */}
                <div className="absolute inset-0 pointer-events-none">
                    {ticks.map((t) => {
                        const major = t % 1000 === 0;
                        const y = yOf(t);
                        return (
                            <div
                                key={t}
                                className="absolute left-0 right-0 flex items-center"
                                style={{ top: y, transform: "translateY(-0.5px)" }}
                            >
                                <div
                                    className="font-mono text-[10px] pl-3 pr-2"
                                    style={{ color: major ? "#A7B4C6" : "#3A4759", width: 80 }}
                                >
                                    {t.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </div>
                                <div
                                    className="flex-1"
                                    style={{
                                        borderTop: major
                                            ? "1px dashed rgba(167,180,198,0.15)"
                                            : "1px dashed rgba(167,180,198,0.06)",
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>

                {/* central spine */}
                <div
                    className="absolute top-0 bottom-0"
                    style={{ left: 96, width: 1, background: "rgba(167,180,198,0.18)" }}
                />

                {/* current price line */}
                <motion.div
                    layout="position"
                    transition={{ type: "spring", stiffness: 200, damping: 28 }}
                    className="absolute left-0 right-0 flex items-center"
                    style={{ top: yOf(price), transform: "translateY(-1px)" }}
                    data-testid="ladder-current-price"
                >
                    <div className="glow-cyan w-full h-[2px]" style={{ background: "#2EE6D6" }} />
                    <div
                        className="absolute font-mono text-[11px] px-2 py-0.5 rounded-md"
                        style={{
                            right: 12,
                            top: -10,
                            background: "#0F1620",
                            border: "1px solid rgba(46,230,214,0.4)",
                            color: "#2EE6D6",
                        }}
                    >
                        ${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                </motion.div>

                {/* BUY markers */}
                <AnimatePresence>
                    {buys.map((b) => {
                        const cat = orderCategory(b, "BUY", false);
                        const col = pal[cat] || LC[cat];
                        return (
                        <motion.div
                            key={`b-${b.grid_id}`}
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -16 }}
                            transition={{ duration: 0.22 }}
                            className="absolute flex items-center gap-2"
                            style={{ top: yOf(b.price), transform: "translateY(-12px)", left: 100 }}
                            data-testid={`ladder-buy-level-${b.grid_id}`}
                        >
                            <div
                                className="w-2 h-2"
                                style={{ background: col, marginLeft: -5, borderRadius: 2 }}
                            />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        className="flex items-center gap-2 px-2 py-1 rounded-md"
                                        style={pillStyle(cat, pal)}
                                    >
                                        <ArrowUp size={12} color={col} />
                                        <span className="font-mono text-[11px]" style={{ color: "#E6EDF6" }}>
                                            ${b.price.toFixed(2)} · {b.qty} {base}
                                        </span>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                    <div className="text-xs font-mono">
                                        <div>Side: BUY · Grid {b.grid_id.slice(0, 8)}</div>
                                        <div style={{ color: col }}>{CAT_LABEL[cat]}</div>
                                        <div>Price: ${b.price.toFixed(2)}</div>
                                        <div>Qty: {b.qty} {base}</div>
                                        <div>Status: {b.status}</div>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </motion.div>
                        );
                    })}
                </AnimatePresence>

                {/* SELL markers */}
                <AnimatePresence>
                    {sells.map((s) => {
                        const trailing = trailSet.has(s.grid_id);
                        const cat = orderCategory(s, "SELL", trailing);
                        const col = pal[cat] || LC[cat];
                        return (
                        <motion.div
                            key={`s-${s.grid_id}`}
                            initial={{ opacity: 0, x: 12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 16 }}
                            transition={{ duration: 0.22 }}
                            className="absolute flex items-center gap-2"
                            style={{ top: yOf(s.price), transform: "translateY(-12px)", left: 100 }}
                            data-testid={`ladder-sell-level-${s.grid_id}`}
                        >
                            <div
                                className="w-2 h-2"
                                style={{ background: col, marginLeft: -5, borderRadius: 2 }}
                            />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        className="flex items-center gap-2 px-2 py-1 rounded-md"
                                        style={pillStyle(cat, pal)}
                                    >
                                        <ArrowDown size={12} color={col} />
                                        <span className="font-mono text-[11px]" style={{ color: "#E6EDF6" }}>
                                            ${s.price.toFixed(2)} · {s.qty} {base}
                                        </span>
                                        {(trailing || MANAGED_EXIT_CATS.has(cat)) && (
                                            <span className="font-mono text-[10px]" style={{ color: col }}>● SL</span>
                                        )}
                                        <span className="font-mono text-[10px] text-[#6F7D92]">
                                            (buy@${s.buy_price.toFixed(2)})
                                        </span>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                    <div className="text-xs font-mono">
                                        <div>Side: SELL · Grid {s.grid_id.slice(0, 8)}</div>
                                        <div style={{ color: col }}>{CAT_LABEL[cat]}</div>
                                        {BUY_KIND_CAT[s.kind] && s.kind !== "grid" && (
                                            <div style={{ color: pal[BUY_KIND_CAT[s.kind]] || LC[BUY_KIND_CAT[s.kind]] }}>
                                                Entry: {CAT_LABEL[BUY_KIND_CAT[s.kind]]}
                                            </div>
                                        )}
                                        <div>Sell @ ${s.price.toFixed(2)}</div>
                                        <div>Original BUY @ ${s.buy_price.toFixed(2)}</div>
                                        {(trailing || MANAGED_EXIT_CATS.has(cat)) && <div style={{ color: col }}>Trailing stop is riding this position up (never below buy+fees)</div>}
                                        <div>Qty: {s.qty} {base}</div>
                                        <div>Status: {s.status}</div>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </motion.div>
                        );
                    })}
                </AnimatePresence>

                {/* Legend — strategy kind (buys) / exit type (sells) actually present */}
                {(legend.buys.length > 0 || legend.sells.length > 0) && (
                    <div
                        className="absolute bottom-2 left-3 right-3 flex flex-wrap items-center gap-x-3 gap-y-1 pointer-events-none"
                        data-testid="ladder-legend"
                    >
                        {legend.buys.map((c) => (
                            <span key={c} className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-[2px]" style={{ background: pal[c] || LC[c] }} />
                                <span className="text-[9px] font-mono" style={{ color: LC.dim }}>
                                    ▲ {CAT_LABEL[c]}
                                </span>
                            </span>
                        ))}
                        {legend.sells.map((c) => (
                            <span key={c} className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-[2px]" style={{ background: pal[c] || LC[c] }} />
                                <span className="text-[9px] font-mono" style={{ color: LC.dim }}>
                                    ▼ {CAT_LABEL[c]}
                                </span>
                            </span>
                        ))}
                    </div>
                )}

                {/* Header label */}
                <div className="absolute top-3 left-4 right-4 flex items-center justify-between pointer-events-none">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6F7D92]">
                        Live Grid Ladder · {String(symbol || "BTCUSDT").toUpperCase()} · {mode.toUpperCase()}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6F7D92]">
                        {buys.length} BUYs / {sells.length} SELLs
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
};

export default GridLadder;
